import { localize } from 'vee-validate';
import * as userStore from '@/modules/user/user.store';
import * as userResource from '@/modules/user/user.resource';

export function searchUsers(searchTerm, pagination = {}) {
  return new Promise((resolve, reject) =>
    userResource
      .searchUsers(searchTerm, pagination)
      .then(({ data }) => {
        resolve({
          data,
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      }).catch(err => reject(err)));
}

export function getUserLanguage() {
  const user = getCurrentStoredUser();
  return user?.language || 'en';
}

export function getCurrentUser() {
  return new Promise((resolve, reject) => {
    userResource.getCurrentUser()
      .then(response => {
        const { data } = response;
        userStore.mutations.setUser(data);
        return resolve();
      })
      .catch(err => {
        userStore.mutations.setUser(null);
        return reject(err);
      });
  });
}

export function getCurrentStoredUser() {
  return userStore.getters.getUser();
}

export function resolveCurrentUser(to, from, next) {
  getCurrentUser().then(() => {
    const language = getUserLanguage();
    localize(language);
    next();
  }).catch(next);
}

export function fetchUsers(pagination = {}) {
  return new Promise((resolve, reject) =>
    userResource
      .fetchUsers(pagination)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function getDepartmentUsers(departmentId) {
  return new Promise((resolve, reject) =>
    userResource
      .getDepartmentUsers(departmentId)
      .then(data => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function fetchBestWriters(limit) {
  return new Promise((resolve, reject) =>
    userResource
      .fetchBestWriters(limit)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function fetchTopReaders(limit) {
  return new Promise((resolve, reject) =>
    userResource
      .fetchTopReaders(limit)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function fetchTopWriters(limit) {
  return new Promise((resolve, reject) =>
    userResource
      .fetchTopWriters(limit)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function fetchRanking(options) {
  return new Promise((resolve, reject) =>
    userResource
      .fetchRanking(options)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function fetchFollowedUsers(user, pagination = {}) {
  return new Promise((resolve, reject) =>
    userResource
      .fetchFollowedUsers(user, pagination)
      .then(({ data }) => {
        resolve({
          data,
          pagination: {
            page: data.page,
            total: parseInt(data.total),
          },
        });
      })
      .catch(err => reject(err)));
}

export function getUserById(id) {
  return new Promise((resolve, reject) =>
    userResource
      .getUserById(id)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function createNewUser(user) {
  return new Promise((resolve, reject) =>
    userResource
      .createNewUser(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function importUsers(csvFile, departmentId) {
  return new Promise((resolve, reject) =>
    loadCSVFile(csvFile, departmentId)
      .then(userList => {
        userResource
          .importUsers(userList)
          .then(result => {
            resolve(result);
          })
          .catch(reject);
      })
      .catch(reject));
}

function loadCSVFile(csvFile, departmentId) {
  return new Promise((resolve, reject) => {
    const userList = [];
    const reader = new FileReader();
    reader.readAsText(csvFile.files[0], 'UTF-8');
    reader.onload = event => {
      const csv = event.target.result;
      const lines = csv.split('\n');
      if (lines[0].trim() !== 'name,last_name,email,position,permission') {
        reject(new Error('FILE_INCORRECT'));
      } else {
        const headers = lines[0].split(',');
        const newHeaders = [];
        headers.forEach((header, index) => {
          newHeaders[index] = header.replace(/(\r\n|\n|\r)|^ /gm, '');
          newHeaders[index] = newHeaders[index].replace(/(")/gm, '');
          newHeaders[index] = newHeaders[index].toLowerCase();
        });

        lines.forEach((line, index) => {
          if (index > 0 && index < lines.length - 1) {
            const inputs = line.split(',');
            if (inputs.length < 5) {
              reject(new Error('FILE_INCORRECT'));
            }
            const newUser = {};
            inputs.forEach((input, inputIndex) => {
              if (!input) {
                reject(new Error('MISSING_INFORMATION'));
              }
              newUser[newHeaders[inputIndex]] = input;
              newUser.department_id = departmentId;
            });
            if (newUser.permission > 2) {
              reject(new Error('NO_PERMISSION'));
            }
            userList.push(newUser);
          }
        });
        resolve(userList);
      }
    };
    reader.onerror = reject;
  });
}

export function updateUserVisits(user) {
  return new Promise((resolve, reject) =>
    userResource
      .updateUserVisits(user)
      .then(data => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function updateUserInformation(user) {
  return new Promise((resolve, reject) =>
    userResource
      .updateUserInformation(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function updateUserPreferences(user) {
  const payload = preparePreferences(user);
  return new Promise((resolve, reject) =>
    userResource
      .updateUserPreferences(payload)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function removeUser(user) {
  return new Promise((resolve, reject) =>
    userResource
      .removeUser(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function unblockUser(user) {
  return new Promise((resolve, reject) =>
    userResource
      .unblockUser(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function blockUser(user) {
  return new Promise((resolve, reject) =>
    userResource
      .blockUser(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function getFollowingStatus(user) {
  return new Promise((resolve, reject) =>
    userResource
      .getFollowingStatus(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function getFollowers(user) {
  return new Promise((resolve, reject) =>
    userResource
      .getFollowers(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function followUser(user) {
  return new Promise((resolve, reject) =>
    userResource
      .followUser(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

export function unfollowUser(user) {
  return new Promise((resolve, reject) =>
    userResource
      .unfollowUser(user)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}
export function uploadUserImage(image) {
  return new Promise((resolve, reject) =>
    userResource.uploadImageFiles(image)
      .then(data => {
        resolve(data);
      })
      .catch(err => reject(err)));
}
export function deleteUserImage(filename) {
  return new Promise((resolve, reject) =>
    userResource
      .deleteFiles(filename)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(err => reject(err)));
}

function preparePreferences(user) {
  return {
    id: user.preferences[0].id,
    new_article: user.preferences[0].new_article,
    article_update: user.preferences[0].article_update,
    new_question: user.preferences[0].new_question,
    new_answer: user.preferences[0].new_answer,
    new_track: user.preferences[0].new_track,
    track_update: user.preferences[0].track_update,
    article_tag: user.preferences[0].article_tag,
    question_tag: user.preferences[0].question_tag,
    article_user: user.preferences[0].article_user,
    question_user: user.preferences[0].question_user,
    followed_track_update: user.preferences[0].followed_track_update,
    email_notification: user.preferences[0].email_notification,
  };
}
